import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useIdleTimer, workerTimers } from "react-idle-timer";
import { Outlet } from "react-router";
import { Typography } from "@mui/material";
import getEnv from "env";
import Button from "components/shared/Button";
import Modal from "components/shared/Modal";
import ModalActionButtons from "components/shared/ModalActionButtons";
import { useLogout } from "context/LogoutContext";
import { DISMISS_IDLE_BROADCAST_CHANNEL_ID } from "utils/constants/broadcastChannel.constants";
import Countdown from "./Countdown";
import { UserSessionTimeoutContext } from "./UserSessionTimeoutContext";

export type TUserSessionTimeoutProviderProps = {
  crossTab?: boolean;
};

type THandleDismissParams = {
  shouldNotifyOthers: boolean;
};

const IDLE_TIMEOUT = Number(getEnv().idleTimeout);
const PROMPT_TIME_PERCENTAGE = 20;
const UPLOAD_ACTIVITY_INTERVAL = Math.min(60_000, IDLE_TIMEOUT / 4);

export const UserSessionTimeoutProvider = ({
  crossTab = true,
}: TUserSessionTimeoutProviderProps) => {
  const { t } = useTranslation("common");
  const { logout } = useLogout();

  const [isPrompted, setIsPrompted] = useState(false);
  const uploadActivityIntervals = useRef(new Set<number>());

  const { reset, message, getRemainingTime, activate } = useIdleTimer({
    timeout: IDLE_TIMEOUT,
    promptBeforeIdle: (IDLE_TIMEOUT * PROMPT_TIME_PERCENTAGE) / 100,
    crossTab,
    timers: workerTimers,
    onPrompt: handlePrompt,
    onIdle: handleIdle,
    onActive: () => setIsPrompted(false),
    name: DISMISS_IDLE_BROADCAST_CHANNEL_ID,
    onMessage: handleCrossTabCommunication,
  });

  function handleCrossTabCommunication(data: { isDismissed: boolean }) {
    if (data.isDismissed && isPrompted) {
      handleDismiss({ shouldNotifyOthers: false });
    }
  }

  function handleDismiss({ shouldNotifyOthers }: THandleDismissParams) {
    reset();
    setIsPrompted(false);

    if (shouldNotifyOthers) {
      message({ isDismissed: true });
    }
  }

  function handleIdle() {
    handleDismiss({ shouldNotifyOthers: true });
    logout({
      shouldRestorePath: true,
      shouldLogoutEverywhere: true,
    }).catch(console.error);
  }

  function handlePrompt() {
    setIsPrompted(true);
  }

  const keepSessionActive = useCallback(() => {
    activate();

    // Activity is shared across tabs, but each upload owns its keep-alive.
    // Closing its tab stops activity without leaving other tabs paused.
    const interval = workerTimers.setInterval(
      activate,
      UPLOAD_ACTIVITY_INTERVAL,
    );

    uploadActivityIntervals.current.add(interval);

    return () => {
      if (uploadActivityIntervals.current.delete(interval)) {
        workerTimers.clearInterval(interval);
        activate();
      }
    };
  }, [activate]);

  useEffect(() => {
    const intervals = uploadActivityIntervals.current;

    return () => {
      intervals.forEach(workerTimers.clearInterval);
      intervals.clear();
    };
  }, []);

  const value = useMemo(() => ({ keepSessionActive }), [keepSessionActive]);

  return (
    <UserSessionTimeoutContext value={value}>
      <Modal open={isPrompted} title={t("messages.logoutWarningTitle")}>
        <Typography
          variant="body1"
          sx={{
            mb: 1.5,
          }}
        >
          <Trans t={t} i18nKey="messages.logoutWarning">
            {isPrompted && <Countdown remainingTime={getRemainingTime()} />}
          </Trans>
        </Typography>
        <ModalActionButtons>
          <Button
            variant="contained"
            onClick={() => handleDismiss({ shouldNotifyOthers: true })}
          >
            {t("button.dismiss")}
          </Button>
        </ModalActionButtons>
      </Modal>
      <Outlet />
    </UserSessionTimeoutContext>
  );
};
